import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { rodadas as apiRodadas } from '../api/endpoints';
import type { StatusRodada } from '../api/tipos';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { Selecao } from '../components/Campo';
import { Carregando, Pagina } from '../components/Pagina';
import { formatarDataHora, formatarInteiro } from '../lib/formatos';
import { SeloStatus } from './Previa';

export function Rodadas() {
  const navegar = useNavigate();
  const [status, setStatus] = useState<StatusRodada | ''>('');
  const { data, isPending, error } = useQuery({
    queryKey: ['rodadas', { status }],
    queryFn: () => apiRodadas.listar({ status: status || undefined }),
  });

  return (
    <Pagina
      titulo="Rodadas de preço"
      contexto="Rascunhos continuam de onde pararam. Rodadas publicadas mostram o preço antes e depois de cada item."
      acoes={
        <Botao variante="principal" onClick={() => navegar('/rodadas/nova')}>
          <Plus aria-hidden className="size-4" /> Nova rodada
        </Botao>
      }
    >
      <Selecao rotulo="Situação" value={status} onChange={(e) => setStatus(e.target.value as StatusRodada | '')} className="mb-3 w-48">
        <option value="">Todas</option>
        <option value="RASCUNHO">Rascunho</option>
        <option value="PUBLICADA">Publicada</option>
        <option value="DESCARTADA">Descartada</option>
      </Selecao>

      {isPending ? (
        <Carregando />
      ) : error ? (
        <Aviso tipo="erro">{error.message}</Aviso>
      ) : data.rodadas.length === 0 ? (
        <Aviso tipo="info">
          Nenhuma rodada ainda.{' '}
          <Link to="/rodadas/nova" className="font-medium text-acao underline">
            Criar a primeira
          </Link>
          .
        </Aviso>
      ) : (
        <div className="overflow-x-auto rounded-painel border border-linha bg-superficie">
          <table className="w-full min-w-[900px] border-collapse text-left">
            <thead className="border-b border-linha text-[13px] text-tinta-2">
              <tr>
                <th className="px-4 py-2.5 font-medium">Rodada</th>
                <th className="px-4 py-2.5 font-medium">Tabela</th>
                <th className="px-4 py-2.5 font-medium">Situação</th>
                <th className="px-4 py-2.5 font-medium">Criada</th>
                <th className="px-4 py-2.5 font-medium">Publicada</th>
                <th className="px-4 py-2.5 text-right font-medium">Itens</th>
                <th className="px-4 py-2.5 text-right font-medium">Alterados</th>
                <th className="px-4 py-2.5 text-right font-medium">Não alterados</th>
              </tr>
            </thead>
            <tbody>
              {data.rodadas.map((r) => (
                <tr key={r.id} className="border-b border-linha-suave last:border-0 hover:bg-fundo/60">
                  <td className="px-4 py-2">
                    <Link to={`/rodadas/${r.id}`} className="font-medium text-acao hover:underline">
                      Rodada {r.id}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <span className="font-mono">{r.tbPreco}</span> <span className="text-tinta-2">{r.descricaoTabela}</span>
                  </td>
                  <td className="px-4 py-2">
                    <SeloStatus status={r.status} />
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatarDataHora(r.criadoEm)}
                    <span className="block text-[12px] text-tinta-2">{r.criadoPor ?? '—'}</span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {r.publicadoEm ? (
                      <>
                        {formatarDataHora(r.publicadoEm)}
                        <span className="block text-[12px] text-tinta-2">{r.publicadoPor ?? '—'}</span>
                      </>
                    ) : (
                      <span className="text-tinta-2">—</span>
                    )}
                  </td>
                  <td className="numero px-4 py-2 text-right">{formatarInteiro(r.qtdItens)}</td>
                  <td className="numero px-4 py-2 text-right font-medium">{formatarInteiro(r.qtdAlterados)}</td>
                  <td className="numero px-4 py-2 text-right text-tinta-2">{formatarInteiro(r.qtdIgnorados)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Pagina>
  );
}
